import {
	GalleryLightboxAds,
	GalleryLightboxAdsHandler,
	insertSlots,
	PlaceholderService,
	QuizSlotsDefinitionRepository,
	slotsContext,
} from '@platforms/shared';
import {
	AdSlotEvent,
	AdSlotStatus,
	communicationService,
	context,
	DiProcess,
	eventsRepository,
	slotService,
	UapLoadStatus,
	universalAdPackage,
	utils,
} from '@wikia/ad-engine';
import { Injectable } from '@wikia/dependency-injection';
import {
	AD_ENGINE_UAP_LOAD_STATUS,
	AD_ENGINE_UAP_NTC_LOADED,
} from '../../../../communication/events/events-ad-engine-uap';
import { UcpDesktopSlotsDefinitionRepository } from './ucp-desktop-slots-definition-repository';
import { exploreNavigationHandler } from '../../utils/explore-navigation-handler';
import { isDesktopFloorAdhesionApplicable } from '../experiments/floor-adhesion-experiment-helper';

@Injectable()
export class UcpDesktopDynamicSlotsSetup implements DiProcess {
	constructor(
		private slotsDefinitionRepository: UcpDesktopSlotsDefinitionRepository,
		private quizSlotsDefinitionRepository: QuizSlotsDefinitionRepository,
		private galleryLightbox: GalleryLightboxAds,
	) {}

	execute(): void {
		this.injectSlots();
		this.configureTopLeaderboardAndCompanions();
		this.configureFloorAdhesionCodePriority();
		this.registerAdPlaceholderService();
		this.handleGalleryLightboxAdsSlots();
	}

	private injectSlots(): void {
		insertSlots([
			this.slotsDefinitionRepository.getTopLeaderboardConfig(),
			this.slotsDefinitionRepository.getTopBoxadConfig(),
			this.slotsDefinitionRepository.getIncontentPlayerConfig(),
			this.slotsDefinitionRepository.getIncontentLeaderboardConfig(),
			this.slotsDefinitionRepository.getBottomLeaderboardConfig(),
		]);

		if (isDesktopFloorAdhesionApplicable()) {
			if (
				context.get('options.isFloorAdhesionNonUapApplicable') ||
				context.get('custom.hasFeaturedVideo') ||
				context.get('custom.hasIncontentPlayer')
			) {
				insertSlots([this.slotsDefinitionRepository.getFloorAdhesionConfig()]);
				slotService.enable('floor_adhesion');
			} else {
				communicationService.on(AD_ENGINE_UAP_NTC_LOADED, () =>
					insertSlots([this.slotsDefinitionRepository.getFloorAdhesionConfig()]),
				);
			}
		}

		// If we're in the experiment, let's add a listener for the sticky right rail experiment
		// since that only gets emitted when the experiment is active. In a future implementation, this will need to get reworked.
		if (utils.isOneOfExperimentsActiveFromContext(['stickyRightRailWithICB'])) {
			communicationService.on(
				eventsRepository.STICKY_RAIL_DESKTOP_MULTI_ICB_READY,
				({ repeatAmount }) => {
					insertSlots([
						this.slotsDefinitionRepository.getMultipleIncontentBoxAdsExperimentConfig(repeatAmount),
					]);
				},
			);
		}

		communicationService.on(eventsRepository.RAIL_READY, () => {
			// We need to wrap this with a rail check to ensure that the unit single ICB unit in Production loads.
			// Since we'll be running the multi-icb in the sticky right rail experiment, we need to add a check to
			// not attempt to add the single ICB unit when the experiment is active.
			if (!utils.isOneOfExperimentsActiveFromContext(['stickyRightRailWithICB'])) {
				insertSlots([this.slotsDefinitionRepository.getIncontentBoxadConfig()]);
			}
			if (context.get('options.replaceOpenWebWithMidBoxAd')) {
				/* Let's insert the mid_boxad slot only if the rail is loaded.
				 * This approach should've been done earlier, similar to how the ICB unit is loaded above. */
				insertSlots([this.slotsDefinitionRepository.getMidBoxAdConfig()]);
			}
		});
		communicationService.on(
			eventsRepository.QUIZ_AD_INJECTED,
			({ slotId }) => {
				insertSlots([this.quizSlotsDefinitionRepository.getQuizAdConfig(slotId)]);
			},
			false,
		);
	}

	private configureTopLeaderboardAndCompanions(): void {
		const slotName = 'top_leaderboard';
		const fvPageReducedSizes = [
			[728, 90],
			[970, 66],
			[970, 90],
			[970, 150],
			[970, 180],
			[970, 250],
		];

		slotsContext.addSlotSize(
			'top_boxad',
			universalAdPackage.UAP_ADDITIONAL_SIZES.companionSizes['5x5'].size,
		);

		if (!context.get('custom.hasFeaturedVideo') || context.get('templates.stickyTlb.withFV')) {
			context.push(`slots.${slotName}.defaultTemplates`, 'stickyTlb');
		}

		if (!context.get('custom.hasFeaturedVideo')) {
			if (context.get('wiki.targeting.pageType') !== 'special') {
				slotsContext.addSlotSize(slotName, universalAdPackage.UAP_ADDITIONAL_SIZES.bfaSize.desktop);
				slotsContext.addSlotSize(slotName, universalAdPackage.UAP_ADDITIONAL_SIZES.bfaSize.unified);
			}

			slotsContext.addSlotSize(
				utils.isOneOfExperimentsActiveFromContext(['stickyRightRailWithICB'])
					? 'incontent_boxad_1'
					: 'incontent_boxad',
				universalAdPackage.UAP_ADDITIONAL_SIZES.companionSizes['5x5'].size,
			);
		} else {
			context.set(`slots.${slotName}.sizes`, [
				{
					viewportSize: [1024, 0],
					sizes: fvPageReducedSizes,
				},
			]);
			context.set('slots.incontent_boxad.defaultSizes', [[300, 250]]);
			slotsContext.addSlotSize(
				'incontent_boxad',
				universalAdPackage.UAP_ADDITIONAL_SIZES.companionSizes['4x4'].size,
			);
		}

		exploreNavigationHandler.init();
	}

	private configureFloorAdhesionCodePriority(): void {
		if (isDesktopFloorAdhesionApplicable()) {
			const slotName = 'floor_adhesion';
			let porvataClosedActive = false;

			communicationService.onSlotEvent(
				AdSlotStatus.STATUS_SUCCESS,
				() => {
					porvataClosedActive = true;

					communicationService.on(AD_ENGINE_UAP_LOAD_STATUS, (action: UapLoadStatus) => {
						if (!action.isLoaded || action.adProduct === 'ruap') {
							communicationService.onSlotEvent(AdSlotEvent.VIDEO_AD_IMPRESSION, () => {
								if (porvataClosedActive) {
									porvataClosedActive = false;
									slotService.disable(slotName);
								}
							});
						}
					});
				},
				slotName,
			);

			communicationService.onSlotEvent(
				AdSlotEvent.HIDDEN_EVENT,
				() => {
					porvataClosedActive = false;
				},
				slotName,
			);
		}
	}

	private registerAdPlaceholderService(): void {
		const placeholderService = new PlaceholderService();
		placeholderService.init();
	}

	private handleGalleryLightboxAdsSlots(): void {
		if (!this.galleryLightbox.initialized) {
			this.galleryLightbox.handler = new GalleryLightboxAdsHandler(this.slotsDefinitionRepository);
			this.galleryLightbox.initialized = true;
		}
		this.galleryLightbox.handler.handle();
	}
}
